import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Invoice } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { useInvoiceCarrierUploadInvoiceProofApi } from '@hero/api-hooks';
import { fileToBase64 } from '@hero/web-utils';
import { formatNumberWithCommas } from '@hero/core';
import { toast } from 'sonner';
import { EditDrawer } from '../../../app/Drawer/EditDrawer';
import { DateField } from '../../../app/Date/DateField';
import { InvoiceNumberInput } from '../../../app/Input/InvoiceNumberInput';
import { Label } from '../../../ui/Label';
import { VatFileField } from '../Shared/VatFileField';

export interface UploadInvoiceProofDrawerProps {
  invoice: Invoice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Override the drawer header copy. The component picks between
   * "Upload" / "Edit" labels based on whether the invoice already
   * carries a VAT PDF (`invoiceProof.image`), but callers (e.g. the
   * financing apply form's "Edit invoice details" CTA) can force
   * either wording with this prop.
   */
  title?: string;
  /** Override the primary button label. See {@link title}. */
  submitLabel?: string;
}

/**
 * Post-create VAT upload drawer.
 *
 * Used from the {@link InvoiceDrawer} summary CTA when the seller
 * wants to attach the actual VAT PDF after the bilateral invoice
 * has already been issued (the create flow also offers an
 * inline upload — see `EligibleLegsDrawer` step 2). Wraps the
 * shared `EditDrawer` chrome and renders the same field
 * primitives the create flow uses (`VatFileField`,
 * `InvoiceNumberInput`, `DateField`) so the two surfaces stay
 * visually identical.
 */
export const UploadInvoiceProofDrawer: React.FC<UploadInvoiceProofDrawerProps> = ({
  invoice,
  open,
  onOpenChange,
  title: titleOverride,
  submitLabel: submitLabelOverride,
}) => {
  const { t } = useTranslation(['m-payment', 'm-invoice', 'c-main']);
  const [files, setFiles] = useState<File[]>([]);
  const [refNumber, setRefNumber] = useState('');
  // Prefill from `invoice.period.due` when the invoice already has
  // one; otherwise leave empty so the seller must pick it.
  const [expectedDueDate, setExpectedDueDate] = useState<Date | undefined>(
    undefined,
  );

  // Has the seller already attached a VAT PDF on a previous save?
  // Used to:
  //  - Skip the "file required" gate on isValid (re-uploading is
  //    optional during an edit).
  //  - Show a hint that the existing file will be preserved if no
  //    new one is picked.
  const existingProofImage = invoice?.invoiceProof?.image;
  const hasExistingFile = !!(existingProofImage?.url || existingProofImage?.upload);

  const { mutateAsync: uploadProof, isPending } = useInvoiceCarrierUploadInvoiceProofApi({
    onSuccess: () => {
      toast.success(t('m-payment:toast.success.invoice_uploaded'));
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(t('m-payment:toast.error.upload_failed', { message: error.message }));
    },
  });

  const handleClose = useCallback(() => {
    setFiles([]);
    setRefNumber('');
    setExpectedDueDate(undefined);
    onOpenChange(false);
  }, [onOpenChange]);

  // Prefill from the invoice every time the drawer opens against a
  // (different) invoice. This makes the drawer behave as a true
  // edit surface — the user sees the current ref / due-date / file
  // state and can adjust just the field they care about.
  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setRefNumber(invoice?.invoiceProof?.refNumber ?? '');
    setExpectedDueDate(
      invoice?.period?.due ? new Date(invoice.period.due) : undefined,
    );
  }, [open, invoice?._id, invoice?.invoiceProof?.refNumber, invoice?.period?.due]);

  const trimmedRef = refNumber.trim();
  const hasNewFile = files.length > 0;
  const fileIsPdf = !hasNewFile || files[0]?.type === 'application/pdf';
  // Either the seller has just picked a new PDF, or there's
  // already one on the invoice from a previous save — both count
  // as "we have a VAT document".
  const hasUsableFile = hasNewFile || hasExistingFile;
  const isValid =
    hasUsableFile && fileIsPdf && trimmedRef.length > 0 && !!expectedDueDate;

  const handleSubmit = useCallback(async () => {
    if (!invoice || !trimmedRef || !expectedDueDate) return;
    if (!hasNewFile && !hasExistingFile) return;
    try {
      // Build the image payload. When the seller picked a new
      // file, encode it to base64 and send as `{ upload }`. When
      // they're editing the ref/date only and the existing PDF
      // should be preserved, forward the persisted `{ id, url }`
      // so the schema's "either upload or id/url" gate passes.
      const image = hasNewFile
        ? { upload: await fileToBase64(files[0]) }
        : {
            id: existingProofImage?.id,
            url: existingProofImage?.url,
          };
      await uploadProof({
        invoiceId: getObjectId(invoice._id),
        image,
        refNumber: trimmedRef,
        expectedDueDate,
      });
    } catch {
      // error surfaced by the mutation's onError handler
    }
  }, [
    invoice,
    files,
    hasNewFile,
    hasExistingFile,
    existingProofImage,
    trimmedRef,
    expectedDueDate,
    uploadProof,
  ]);

  const defaultTitle = hasExistingFile
    ? t('m-payment:invoice.proof.title_edit', {
        defaultValue: 'Edit invoice details',
      })
    : t('m-payment:invoice.proof.title', {
        defaultValue: 'Upload VAT invoice',
      });
  const defaultSubmitLabel = hasExistingFile
    ? t('m-payment:invoice.proof.submit_edit', {
        defaultValue: 'Save changes',
      })
    : t('m-payment:invoice.proof.submit', {
        defaultValue: 'Upload Invoice',
      });

  return (
    <EditDrawer
      isOpen={open}
      onClose={handleClose}
      title={titleOverride ?? defaultTitle}
      submitLabel={submitLabelOverride ?? defaultSubmitLabel}
      submitDisabled={!isValid}
      isSubmitting={isPending}
      onSubmit={handleSubmit}>
      <div className="flex flex-col gap-5">
        {invoice && (
          <div className="flex items-start gap-3 rounded-lg bg-muted/40 p-4">
            <svg
              className="size-4 text-primary mt-0.5 shrink-0"
              viewBox="0 0 16 16"
              fill="currentColor">
              <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 8a.75.75 0 01-1.5 0V5a.75.75 0 011.5 0v3z" />
            </svg>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('m-payment:invoice.proof.vat_note', {
                ref: invoice.ref?.main || invoice.ref?.short || '',
                amount: formatNumberWithCommas(invoice.amount.total),
                currency: invoice.amount.currency || 'SAR',
              })}
            </p>
          </div>
        )}

        <VatFileField
          id="upload-proof-vat-file"
          files={files}
          onChange={setFiles}
          required={!hasExistingFile}
          helperText={
            hasExistingFile && files.length === 0
              ? t('m-invoice:upload_proof.existing_file_hint', {
                  defaultValue:
                    'A VAT PDF is already attached to this invoice. Pick a new file to replace it, or leave this empty to keep it.',
                })
              : undefined
          }
        />

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="upload-proof-ref">
            {t('m-payment:invoice.proof.ref_label', {
              defaultValue: 'Invoice number',
            })}
            <span className="text-destructive ms-1">*</span>
          </Label>
          <InvoiceNumberInput
            id="upload-proof-ref"
            value={refNumber}
            onChange={setRefNumber}
            placeholder={t('m-payment:invoice.proof.ref_placeholder', {
              defaultValue: 'Your accounting reference',
            })}
            disabled={isPending}
          />
        </div>

        <div className="flex flex-col gap-1">
          <DateField
            label={t('m-invoice:create_invoice.expected_due_date', {
              defaultValue: 'Expected pay date',
            })}
            required
            value={expectedDueDate ?? null}
            onChange={setExpectedDueDate}
            placeholder={t('m-invoice:upload_proof.expected_due_date_placeholder', {
              defaultValue: 'Pick the date the buyer is expected to pay',
            })}
            disabled={isPending}
          />
          <p className="text-xs text-muted-foreground">
            {t('m-invoice:upload_proof.expected_due_date_hint_required', {
              defaultValue:
                'Required. This pay date matches the invoice you already issued and overrides the existing due date.',
            })}
          </p>
        </div>
      </div>
    </EditDrawer>
  );
};
